import unescapeHTML from './unescape-html.tsx';

export type QueryId = 'earlyFetchMainIssueAggQuery' | 'earlyFetchIssueViewInteractiveQuery';

/**
 * Scrapes the JSON payload from the initial page response within as HTML comment by bifrost template.
 *
 * @param queryId - The query ID to scrape the JSON payload for. Only supports 'earlyFetchMainIssueAggQuery' or 'earlyFetchIssueViewInteractiveQuery'.
 * The values are accessible via IssueViewFetchOperations.EARLY_FETCH_GIRA or IssueViewFetchOperations.EARLY_FETCH_AGG.
 * @returns Promise of JSON payload scraped from HTML comment.
 */
const scrapeJsonFromInitialPageResponse = (queryId: QueryId): Promise<Object> => {
	if (!queryId) return Promise.reject();

	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	const checkForEl = () => document.getElementById(`shifted-${queryId}`);
	const DIDNT_ARRIVE = new Error(`element with ID "shifted-${queryId}" did not arrive`);

	return new Promise((resolve, reject) => {
		const tryResolve = (el: HTMLElement | null) => {
			try {
				let payloadEl = el?.previousSibling;
				// account for server sending whitespace before the comment
				if (payloadEl && payloadEl.nodeType === Node.TEXT_NODE) {
					payloadEl = payloadEl.previousSibling;
				}
				// payload should be sent in a comment node
				if (!payloadEl || payloadEl.nodeType !== Node.COMMENT_NODE) {
					throw new Error(`shifted ${queryId}: expected comment node before shifted element`);
				}
				// the comment node should have non-empty text content
				if (!payloadEl.textContent) {
					throw new Error(`shifted ${queryId}: expected comment node to have text content`);
				}
				// parse the content as json, expect an object in response
				// JIV-19357: Unescape angle brackets that was escaped from SSR to avoid HTML comment breakages.
				let result;
				try {
					result = JSON.parse(unescapeHTML(payloadEl.textContent));
				} catch (e) {
					throw new Error(
						`shifted ${queryId}: expected comment node to have text parsable to JSON`,
					);
				}

				// bail if the server didn't send an object as JSON
				// add null check since apparently 'null' is JSON parsable and typeof null is object
				if (typeof result !== 'object' || result === null) {
					throw new Error(`shifted ${queryId}: expected JSON payload to be a non-null object`);
				}
				// bail if the server reported any problems
				if (result.ssrError) {
					throw new Error(result.ssrError);
				}

				// bail JSON payload does not have data
				if (!result.data) {
					throw new Error(`shifted ${queryId}: expected JSON payload to have data`);
				}
				resolve(result);
			} catch (e) {
				reject(e);
			}
		};

		// check for the element immediately.
		{
			const el = checkForEl();
			if (el) {
				tryResolve(el);
				return;
			}
		}

		// if the document is ready, the element either exists or didn't arrive.
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		if (document.readyState === 'interactive' || document.readyState === 'complete') {
			reject(DIDNT_ARRIVE);
			return;
		}

		// wait for the element to arrive.
		const onNewElements: MutationCallback = (_, observer) => {
			// I could be "optimal" by iterating through the elements indicated in the mutation record array...
			// ...but it's probably just faster to ask the DOM whether the element exists by ID now.
			const el = checkForEl();
			if (el) {
				observer.disconnect();
				tryResolve(el);
			}
		};
		// use a mutation observer until the document is ready.
		// only observe direct children of the document body.
		const observer = new MutationObserver(onNewElements);
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		observer.observe(document.body, { attributes: false, childList: true, subtree: false });

		// only keep trying until DOM ready, then give up.
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		document.addEventListener(
			'DOMContentLoaded',
			() => {
				setTimeout(() => {
					observer.disconnect();
					const el = checkForEl();
					el ? tryResolve(el) : reject(DIDNT_ARRIVE);
				}, 0);
			},
			{ once: true },
		);
	});
};

export default scrapeJsonFromInitialPageResponse;
